<template>
  <section class="p-6 flex flex-col gap-6">
    <nav class="text-gray-600 text-sm">
      <span class="font-semibold cursor-pointer" @click="router.push('/')">
        Home
      </span>
      <span class="mx-2">/</span>
      <span class="cursor-pointer font-semibold" @click="router.push({ name: 'Languages' })">
        Languages
      </span>
      <span class="mx-2">/</span>
      <span class="text-gray-800 font-bold">Edit Language</span>
    </nav>

    <h3 class="font-bold text-2xl">Edit Language #{{ id }}</h3>

    <div
      v-if="errors.length"
      class="bg-red-50 border border-red-200 text-red-700 p-3 rounded-md text-sm"
    >
      <ul class="list-disc list-inside">
        <li v-for="(err, idx) in errors" :key="idx">{{ err }}</li>
      </ul>
    </div>

    <div v-if="loading" class="text-sm text-gray-500">
      Loading language...
    </div>

    <form
      v-else
      @submit.prevent="submitForm"
      class="bg-white shadow rounded-lg p-6 flex flex-col gap-6"
    >
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label class="block text-sm font-medium mb-1">Name (Arabic)</label>
          <input
            v-model="form.name_ar"
            class="border w-full rounded-md px-3 py-2"
          />
        </div>
        <div>
          <label class="block text-sm font-medium mb-1">Name (English)</label>
          <input
            v-model="form.name_en"
            class="border w-full rounded-md px-3 py-2"
          />
        </div>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label class="block text-sm font-medium mb-1">Code</label>
          <input
            v-model="form.code"
            class="border w-full rounded-md px-3 py-2"
          />
        </div>
        <div>
          <label class="block text-sm font-medium mb-1">Sort Order</label>
          <input
            v-model.number="form.sort_order"
            type="number"
            min="0"
            class="border w-full rounded-md px-3 py-2"
          />
        </div>
      </div>

      <div>
        <label class="inline-flex items-center gap-2 text-sm">
          <input type="checkbox" v-model="form.is_active" />
          <span>Active</span>
        </label>
      </div>

      <div class="flex justify-end gap-3">
        <button
          type="button"
          class="px-4 py-2 border rounded-md"
          @click="router.push({ name: 'Languages' })"
          :disabled="isSubmitting"
        >
          Cancel
        </button>
        <button
          type="submit"
          class="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-60"
          :disabled="isSubmitting"
        >
          <span v-if="isSubmitting">Saving...</span>
          <span v-else>Save Changes</span>
        </button>
      </div>
    </form>
  </section>
</template>

<script setup>
import { ref, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import apiClient from "@/apiClient";

const route = useRoute();
const router = useRouter();
const id = route.params.id;

const form = ref({
  name_ar: "",
  name_en: "",
  code: "",
  sort_order: 0,
  is_active: true,
});

const loading = ref(false);
const errors = ref([]);
const isSubmitting = ref(false);

async function fetchLanguage() {
  try {
    loading.value = true;

    const res = await apiClient.get(`/languages/${id}`);
    const data = res.data?.data;
    form.value = {
      name_ar: data.name_ar,
      name_en: data.name_en,
      code: data.code,
      sort_order: data.sort_order ?? 0,
      is_active: !!data.is_active,
    };
  } catch (error) {
    console.error("Error fetching language:", error);
    errors.value.push("Failed to load language.");
  } finally {
    loading.value = false;
  }
}

function validate() {
  errors.value = [];
  if (!form.value.name_ar.trim()) errors.value.push("Arabic name is required.");
  if (!form.value.name_en.trim()) errors.value.push("English name is required.");
  if (!form.value.code.trim()) errors.value.push("Code is required.");
  return errors.value.length === 0;
}

async function submitForm() {
  try {
    if (!validate()) return;
    isSubmitting.value = true;

    await apiClient.post(`/languages/${id}`, form.value);
    router.push({ name: "Languages" });
  } catch (error) {
    console.error("Error updating language:", error);
    if (error.response?.data) {
      const data = error.response.data;
      if (data.message) errors.value.push(data.message);
      if (data.errors) {
        Object.values(data.errors).forEach((msgs) =>
          msgs.forEach((m) => errors.value.push(m))
        );
      }
    } else {
      errors.value.push("Failed to update language.");
    }
  } finally {
    isSubmitting.value = false;
  }
}

onMounted(fetchLanguage);
</script>
